import { buildAuditSearchOr } from './auditEventSearchFields';

// Audit-log writes for tenant-wide significant actions.
//
// RD-BILL-007: every action that an admin might want to investigate later
// (or that compliance/disputes might require) writes an immutable row into
// `public.audit_events`. The trail is read by the Activity page (top-level
// admin tab) and powers cross-entity investigation.
//
// All writes here are FAIL-SOFT. The audit log is a side-channel; if its
// write fails (transient DB error, missing migration on dev, rare RLS
// edge-case) the originating CRUD operation MUST still succeed. We log
// to console AND report to Sentry — a silent fail-soft once hid a real bug
// (company_settings.id 'default' written into the uuid entity_id column →
// 22P02 → every settings audit event silently dropped). Surfacing failures
// to observability turns "events mysteriously missing" into an alert.
//
// `entity_type` is whitelisted at the DB level via CHECK constraint —
// extending the set requires a migration. `event_type` is free-form so
// new actions can be instrumented without schema changes.

import { supabase } from '../supabase';
import { captureException } from '../utils/error/sentryClient';

/** Whitelist of entity types — must match the DB CHECK constraint
 *  (migration 20260509130000_audit_events_unify.sql). Extending these
 *  requires altering the constraint. */
export const AUDIT_ENTITY_TYPES = Object.freeze({
  INVOICE: 'invoice',
  ESTIMATE: 'estimate',
  CUSTOMER: 'customer',
  JOB: 'job',
  NO_SERVICE: 'no_service',
  EMPLOYEE: 'employee',
  AUTH: 'auth',
  SCHEDULE: 'schedule',
  ROUTE_OVERRIDE: 'route_override',
  CATALOG: 'catalog',
  SETTINGS: 'settings',
  BULK_OP: 'bulk_op',
  SYSTEM: 'system',
  // Server-only self-audit — written by mark_security_events_reviewed
  // (RD-SEC — the admin-only "N security events to review" dashboard row).
  // Never client-emitted.
  SECURITY: 'security',
  TENANT: 'tenant', // KAN-200: server-written company-access lock events
});

/** Canonical event-type strings per entity. Free-form at the DB level —
 *  new strings can be added without a migration. Documented here so the
 *  Activity page filter dropdown stays in sync. */
export const AUDIT_EVENT_TYPES = Object.freeze({
  // Invoice
  INVOICE_CREATED: 'created',
  INVOICE_SENT: 'sent',
  INVOICE_PAID: 'paid',
  INVOICE_VOIDED: 'voided',
  INVOICE_DELETED: 'deleted',
  INVOICE_STATUS_CHANGED: 'status_changed',
  INVOICE_EDITED: 'edited',
  INVOICE_JOBS_ADDED: 'jobs_added',
  INVOICE_REMINDER_SENT: 'reminder_sent',
  INVOICE_SEND_FAILED: 'send_failed',
  INVOICE_VOID_NOTIFICATION_SKIPPED: 'void_notification_skipped',
  INVOICE_VOID_NOTIFICATION_SENT: 'void_notification_sent',
  INVOICE_REMINDERS_PAUSED: 'reminders_paused',
  INVOICE_REMINDERS_RESUMED: 'reminders_resumed',
  // Server-only self-audit (20260925120000) — written by
  // guard_estimate_delete_invoice_link when a hard-deleted estimate's
  // invoice link is cleared. Never client-emitted; deliberately absent from
  // public.audit_client_event_allowed.
  INVOICE_ESTIMATE_LINK_CLEARED: 'estimate_link_cleared',

  // Estimate
  ESTIMATE_CREATED: 'created',
  ESTIMATE_SENT: 'sent',
  ESTIMATE_ACCEPTED: 'accepted',
  ESTIMATE_DECLINED: 'declined',
  ESTIMATE_CONVERTED: 'converted_to_invoice',
  ESTIMATE_ARCHIVED: 'archived',
  ESTIMATE_DELETED: 'deleted',
  ESTIMATE_EDITED: 'edited',
  ESTIMATE_SEND_FAILED: 'send_failed',
  // RD-EST-RESP-001. Emitted SERVER-SIDE by public.revoke_estimate_response_link
  // (20260831120000), never from the client — the constant exists so
  // eventDisplayMeta.test.js derives the render assertion from it like every
  // other event. lowercase_snake is the convention; the SCREAMING_SNAKE
  // ESTIMATE_* values below are legacy drift, not the pattern to copy.
  ESTIMATE_LINK_REVOKED: 'link_revoked',
  ESTIMATE_EXPIRATION_EXTENDED: 'ESTIMATE_EXPIRATION_EXTENDED',
  ESTIMATE_CLONED: 'ESTIMATE_CLONED',
  ESTIMATE_RETURNED_TO_SENT: 'ESTIMATE_RETURNED_TO_SENT',
  ESTIMATE_RETURNED_TO_DRAFT: 'ESTIMATE_RETURNED_TO_DRAFT',
  ESTIMATE_RESTORED: 'ESTIMATE_RESTORED',
  ESTIMATE_RESEND_AFTER_EXTEND: 'ESTIMATE_RESEND_AFTER_EXTEND',
  ESTIMATE_INVOICE_ORPHAN_REPAIR: 'ESTIMATE_INVOICE_ORPHAN_REPAIR',

  // Customer
  CUSTOMER_CREATED: 'created',
  CUSTOMER_EDITED: 'edited',
  CUSTOMER_LIFECYCLE_CHANGED: 'lifecycle_changed',
  CUSTOMER_ARCHIVED: 'archived',
  CUSTOMER_DELETED: 'deleted',
  CUSTOMER_CC_EMAILS_UPDATED: 'cc_emails_updated',
  CUSTOMER_CONVERTED_TO_RECURRING: 'converted_to_recurring',
  CUSTOMER_BULK_IMPORTED: 'customer_bulk_imported',
  CUSTOMER_DUPLICATE_RESOLVED: 'duplicate_resolved',

  // Employee
  EMPLOYEE_INVITED: 'invited',
  EMPLOYEE_INVITE_ACCEPTED: 'invite_accepted',
  EMPLOYEE_ROLE_CHANGED: 'role_changed',
  EMPLOYEE_DEACTIVATED: 'deactivated',
  EMPLOYEE_REACTIVATED: 'reactivated',
  EMPLOYEE_MFA_DISABLED: 'mfa_disabled',

  // Auth
  AUTH_LOGIN_SUCCESS: 'login_success',
  AUTH_LOGIN_FAILED: 'login_failed',
  AUTH_LOGOUT: 'logout',
  AUTH_MFA_ENROLLED: 'mfa_enrolled',
  AUTH_PASSWORD_RESET_SENT: 'password_reset_sent',
  AUTH_PASSWORD_CHANGED: 'password_changed',
  AUTH_MFA_EMAIL_RECOVERY_SENT: 'mfa_email_recovery_sent',
  AUTH_MFA_EMAIL_RECOVERY_USED: 'mfa_email_recovery_used',
  AUTH_MFA_VERIFY_FAILED: 'mfa_verify_failed',
  AUTH_MFA_DISABLED: 'mfa_disabled',
  // RD-SEC-015: written server-side only (mfa_sweep_emit /
  // replace_mfa_recovery_codes — see supabase/migrations/
  // 20260925140300_mfa_factor_lifecycle_sweeper.sql and
  // 20260925140200_replace_mfa_recovery_codes_rpc.sql). No client write site.
  AUTH_MFA_FACTOR_ADDED: 'mfa_factor_added',
  AUTH_MFA_FACTOR_REMOVED: 'mfa_factor_removed',
  AUTH_MFA_RECOVERY_CODES_GENERATED: 'mfa_recovery_codes_generated',
  AUTH_MFA_RECOVERY_CODES_REGENERATED: 'mfa_recovery_codes_regenerated',

  // Catalog (services / chemicals)
  CATALOG_CREATED: 'created',
  CATALOG_EDITED: 'edited',

  // Job
  JOB_LOGGED_ON_BEHALF_OF: 'job_logged_on_behalf_of',
  JOB_DUPLICATE_SERVICE_OVERRIDE: 'duplicate_service_override',
  JOB_SAME_DAY_VISIT_OVERRIDE: 'same_day_visit_override',
  JOB_OFF_PERIOD_SERVICE_OVERRIDE: 'off_period_service_override',
  JOB_DUPLICATE_LINE_REMOVED: 'duplicate_line_removed',
  // SQL emitter: price_job_line_item (20260909120200)
  JOB_LINE_ITEM_PRICED: 'line_item_priced',

  // Settings
  SETTINGS_MFA_FLOOR_CHANGED: 'mfa_floor_changed',
  SETTINGS_MFA_POLICY_CHANGED: 'mfa_policy_changed',
  // SQL emitter: audit_price_visibility_change trigger (20260909120300)
  SETTINGS_PRICE_VISIBILITY_CHANGED: 'price_visibility_changed',
  /* RD-EST-RESP-001 (migration 20260831120000, PHASE 2+ OWES item 15) — the
     tenant switch that decides whether estimate emails carry a public
     approve/decline link. SERVER-emitted since 20260929120000
     (update_company_settings; its peer mfa_policy_changed by an AFTER UPDATE OF
     mfa_required_roles trigger) and removed from audit_client_event_allowed.
     The client still writes both ONLY as the deploy-order legacy fallback
     (capability 'legacy', see companySettingsRpcCapability.js). */
  SETTINGS_ESTIMATE_RESPONSES_POLICY_CHANGED: 'estimate_responses_policy_changed',
  /* RD-EST-RESP-001 — the tenant's "also notify" list changed (WHO ELSE is
     mailed when a customer approves or declines, on top of the sender and the
     owner). A separate event from the toggle above because it answers a
     different question: not "can customers respond?" but "who was told?" —
     which matters the moment somebody says nobody knew about an approval. */
  SETTINGS_ESTIMATE_RESPONSE_NOTIFY_CHANGED: 'estimate_response_notify_changed',
  /* RD-ROLE-006 — the Office Inbox "also notify" list changed. Same shape
     and same reason as the estimate-response twin above: "who was told a
     price is needed?" is not answerable from a diffed field name. */
  SETTINGS_OFFICE_TASK_NOTIFY_CHANGED: 'office_task_notify_changed',
  SETTINGS_TENANT_PROFILE_CHANGED: 'tenant_profile_changed',
  SETTINGS_TENANT_ENTITLEMENTS_CHANGED: 'tenant_entitlements_changed',
  // KAN-200 company access lock (entity 'tenant', server-written)
  TENANT_COMPANY_ACCESS_LEVEL_CHANGED: 'company_access_level_changed',
  TENANT_COMPANY_ACCESS_READ_ONLY_SOON: 'company_access_read_only_soon',
  TENANT_COMPANY_TRIAL_ENDING_SOON: 'company_trial_ending_soon',
  // Backdate override (RD-SEC-001 Phase 4) — shared string, differentiated by entity_type
  JOB_BACKDATE_OVERRIDE: 'backdate_override',
  NO_SERVICE_BACKDATE_OVERRIDE: 'backdate_override',

  // Bulk ops
  BULK_OP_BULK_CHANGE_TYPE_CATALOG_RUN: 'bulk_change_type_catalog_run',

  // Schedule
  SCHEDULE_SHIFT_DELETED: 'shift_deleted',
  SCHEDULE_SHIFT_EDITED: 'shift_edited',

  // Security — server-only self-audit written by
  // mark_security_events_reviewed. Payload carries {reviewed_through}.
  SECURITY_EVENTS_REVIEWED: 'security_events_reviewed',
});

/**
 * Generic audit-log writer. Fail-soft — never throws.
 *
 * @param {object} args
 * @param {string} args.tenantId    Required — RLS scope.
 * @param {string} args.entityType  One of AUDIT_ENTITY_TYPES (whitelist enforced at DB level).
 * @param {string|null} [args.entityId]  Affected row id; null for entity-less events.
 * @param {string} args.eventType   Short kind identifier.
 * @param {object} [args.payload]   Event-specific context.
 * @param {string} [args.actorName] Human-readable actor label snapshot.
 * @param {string} [args.actorRole] Role snapshot ('admin' / 'billing' / etc.).
 * @returns {Promise<{ ok: boolean, error: Error | null }>}
 */
export async function writeAuditEvent({
  tenantId,
  entityType,
  entityId = null,
  eventType,
  payload = {},
  actorName = null,
  actorRole = null,
}) {
  if (!tenantId || !entityType || !eventType) {
    return { ok: false, error: new Error('writeAuditEvent: tenantId, entityType, and eventType are required') };
  }
  // entity_id is a uuid column. A non-uuid value (e.g. a singleton text key
  // like company_settings.id = 'default') makes Postgres reject the INSERT
  // with 22P02 — and because most callers fire-and-forget this helper, that
  // error is invisible and the audit event is silently lost. Coerce an invalid
  // id to null (the column is nullable) and surface the caller bug LOUDLY
  // instead of dropping the event. Guard: forcedMfaEnrollment-style purity not
  // needed — this is a defensive coercion at the write boundary.
  // Shared Sentry reporter so any audit-write problem is visible in
  // observability instead of dying in a console no one is watching.
  const reportToSentry = (err, level) => {
    captureException(err instanceof Error ? err : new Error(String(err?.message || err)), {
      level,
      tags: { area: 'audit_events', audit_event_type: eventType },
      extra: { tenantId, entityType, eventType, entityId },
    });
  };

  let safeEntityId = entityId;
  if (entityId != null
    && !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(String(entityId))) {
    console.error(`[audit_events] entityId is not a uuid ("${entityId}") for ${entityType}:${eventType} — writing with entity_id=null. Fix the caller.`);
    reportToSentry(new Error(`audit_events: non-uuid entityId ("${entityId}")`), 'warning');
    safeEntityId = null;
  }
  try {
    const { error } = await supabase
      .from('audit_events')
      .insert({
        tenant_id: tenantId,
        entity_type: entityType,
        entity_id: safeEntityId,
        event_type: eventType,
        actor_name: actorName,
        actor_role: actorRole,
        payload,
      });
    if (error) {
      console.warn('[audit_events] write failed:', error.message || error);
      reportToSentry(new Error(`audit_events insert rejected: ${error.message || error.code || 'unknown'}`), 'error');
      return { ok: false, error };
    }
    return { ok: true, error: null };
  } catch (err) {
    console.warn('[audit_events] write threw:', err);
    reportToSentry(err, 'error');
    return { ok: false, error: err };
  }
}

/**
 * Read the chronological event trail (newest first), optionally filtered.
 * Used by the Activity page. RLS limits to admin (all entities) + billing
 * (invoice / estimate / customer only).
 *
 * @param {object} args
 * @param {string} [args.entityType]   Filter to one entity kind.
 * @param {string} [args.entityId]     When set, scopes to one specific row.
 * @param {string} [args.eventType]    Filter to one event kind.
 * @param {string} [args.actorId]      Filter to one actor (auth.uid()).
 * @param {string} [args.search]       Free-text on actor_name / event_type.
 * @param {string} [args.fromIso]      ISO timestamp lower bound (inclusive).
 * @param {string} [args.toIso]        ISO timestamp upper bound (inclusive).
 * @param {string} [args.batchId]      When set, scopes to one bulk-send batch
 *                                     (matches payload->>'batch_id').
 * @param {number} [args.limit]        Default 50.
 * @param {number} [args.offset]       For pagination.
 * @returns {Promise<{ data: Array<object>, error: Error | null }>}
 */
export async function listAuditEvents({
  entityType,
  entityId,
  eventType,
  actorId,
  search,
  fromIso,
  toIso,
  batchId,
  limit = 50,
  offset = 0,
} = {}) {
  let q = supabase
    .from('audit_events')
    .select('id, tenant_id, entity_type, entity_id, event_type, actor_id, actor_name, actor_role, payload, created_at')
    .order('created_at', { ascending: false })
    .range(offset, offset + limit - 1);

  // Accept single value OR array for entityType / eventType. Array form
  // powers the export modal's multi-select; single form preserves the
  // existing call sites (Activity page filter bar).
  if (Array.isArray(entityType) && entityType.length > 0) q = q.in('entity_type', entityType);
  else if (entityType)                                    q = q.eq('entity_type', entityType);
  if (entityId) q = q.eq('entity_id', entityId);
  if (Array.isArray(eventType) && eventType.length > 0) q = q.in('event_type', eventType);
  else if (eventType)                                   q = q.eq('event_type', eventType);
  if (actorId === 'system') q = q.is('actor_id', null);
  else if (actorId)         q = q.eq('actor_id', actorId);
  if (fromIso)    q = q.gte('created_at', fromIso);
  if (toIso)      q = q.lte('created_at', toIso);
  if (batchId)    q = q.filter('payload->>batch_id', 'eq', batchId);
  const searchOr = buildAuditSearchOr(search);
  if (searchOr) q = q.or(searchOr);

  const { data, error } = await q;
  return { data: data || [], error: error || null };
}

/**
 * Count rows matching the same filter shape as listAuditEvents /
 * listAuditEventsByCursor. Uses `head: true` so the response is just the
 * count — no row payload. Filter-aware (unlike pg_class.reltuples), but
 * may slow down past ~100k matching rows. Switch to a capped count or a
 * cached running total when that becomes a concern.
 *
 * Returns:
 *   { count: number, error: Error | null }
 */
export async function countAuditEvents({
  entityType,
  entityId,
  eventType,
  actorId,
  search,
  fromIso,
  toIso,
  batchId,
} = {}) {
  let q = supabase
    .from('audit_events')
    .select('*', { count: 'exact', head: true });
  if (Array.isArray(entityType) && entityType.length > 0) q = q.in('entity_type', entityType);
  else if (entityType)                                    q = q.eq('entity_type', entityType);
  if (entityId) q = q.eq('entity_id', entityId);
  if (Array.isArray(eventType) && eventType.length > 0) q = q.in('event_type', eventType);
  else if (eventType)                                   q = q.eq('event_type', eventType);
  if (actorId === 'system') q = q.is('actor_id', null);
  else if (actorId)         q = q.eq('actor_id', actorId);
  if (fromIso)    q = q.gte('created_at', fromIso);
  if (toIso)      q = q.lte('created_at', toIso);
  if (batchId)    q = q.filter('payload->>batch_id', 'eq', batchId);
  const searchOr = buildAuditSearchOr(search);
  if (searchOr) q = q.or(searchOr);
  const { count, error } = await q;
  return { count: count ?? 0, error: error || null };
}

/**
 * Cursor-paginated read for the Activity page feed. Same filter shape as
 * `listAuditEvents` but takes a `cursor` ({ createdAt, id }) instead of
 * `offset`. Cursor pagination is O(log n) — `OFFSET 10000 LIMIT 50` would
 * scan-and-discard 10K rows on every page and degrade as the table grows.
 *
 * Returns:
 *   { data, nextCursor, error }
 *
 * `nextCursor` is null when there are no more rows (last page reached).
 * Callers pass it back as `cursor` on the next call to load the next page.
 *
 * Compound cursor on (created_at, id) handles the rare-but-possible case
 * of two events at the same microsecond — without the secondary `id`
 * key we'd silently skip duplicates at page boundaries.
 *
 * @returns {Promise<{ data: Array, nextCursor: { createdAt, id } | null, error: Error | null }>}
 */
export async function listAuditEventsByCursor({
  entityType,
  entityId,
  eventType,
  actorId,
  search,
  fromIso,
  toIso,
  batchId,
  limit = 50,
  cursor = null,
  sortOrder = 'desc',
} = {}) {
  const ascending = sortOrder === 'asc';
  let q = supabase
    .from('audit_events')
    .select('id, tenant_id, entity_type, entity_id, event_type, actor_id, actor_name, actor_role, payload, created_at')
    .order('created_at', { ascending })
    .order('id', { ascending })
    // Fetch limit+1 so we know whether a next page exists without a
    // separate count query. The +1 row is sliced off before returning.
    .limit(limit + 1);

  if (Array.isArray(entityType) && entityType.length > 0) q = q.in('entity_type', entityType);
  else if (entityType)                                    q = q.eq('entity_type', entityType);
  if (entityId) q = q.eq('entity_id', entityId);
  if (Array.isArray(eventType) && eventType.length > 0) q = q.in('event_type', eventType);
  else if (eventType)                                   q = q.eq('event_type', eventType);
  if (actorId === 'system') q = q.is('actor_id', null);
  else if (actorId)         q = q.eq('actor_id', actorId);
  if (fromIso)    q = q.gte('created_at', fromIso);
  if (toIso)      q = q.lte('created_at', toIso);
  if (batchId)    q = q.filter('payload->>batch_id', 'eq', batchId);
  const searchOr = buildAuditSearchOr(search);
  if (searchOr) q = q.or(searchOr);
  if (cursor?.createdAt && cursor?.id) {
    // Cursor comparison flips with sort order: descending → "less than
    // cursor", ascending → "greater than cursor". Compound (created_at,
    // id) tiebreaker matches the order-by.
    if (ascending) {
      q = q.or(
        `created_at.gt.${cursor.createdAt},and(created_at.eq.${cursor.createdAt},id.gt.${cursor.id})`,
      );
    } else {
      q = q.or(
        `created_at.lt.${cursor.createdAt},and(created_at.eq.${cursor.createdAt},id.lt.${cursor.id})`,
      );
    }
  }

  const { data, error } = await q;
  if (error) return { data: [], nextCursor: null, error };

  const rows = data || [];
  const hasMore = rows.length > limit;
  const page = hasMore ? rows.slice(0, limit) : rows;
  const last = page[page.length - 1];
  const nextCursor = hasMore && last
    ? { createdAt: last.created_at, id: last.id }
    : null;

  return { data: page, nextCursor, error: null };
}

/**
 * Paginated full fetch for the export modal. Loops `listAuditEvents`
 * until exhausted or the safety cap is reached. Capped at 10,000 rows
 * per export — anything beyond is realistically a server-side report job
 * (defer until a customer asks for it). Returns whether the cap was hit
 * so the caller can warn the user.
 *
 * @param {object} filters Same shape as listAuditEvents (supports arrays
 *                         for entityType / eventType).
 * @param {number} [maxRows=10000] Safety cap.
 * @returns {Promise<{ events: Array, capped: boolean, error: Error | null }>}
 */
export async function fetchAllAuditEvents(filters = {}, maxRows = 10000) {
  const PAGE = 200;
  const out = [];
  let offset = 0;
  while (out.length < maxRows) {
    const remaining = maxRows - out.length;
    const limit = Math.min(PAGE, remaining);
    // eslint-disable-next-line no-await-in-loop
    const { data, error } = await listAuditEvents({ ...filters, limit, offset });
    if (error) return { events: out, capped: false, error };
    if (!data || data.length === 0) break;
    out.push(...data);
    if (data.length < limit) break;
    offset += data.length;
  }
  return { events: out, capped: out.length >= maxRows, error: null };
}

/**
 * Single-entity convenience wrapper used by the per-invoice activity panel
 * (which still mounts inside the invoice preview drawer for in-context
 * lookups). The Activity page uses listAuditEvents directly.
 */
export async function listEventsForEntity(entityType, entityId, { limit = 100 } = {}) {
  if (!entityType || !entityId) return { data: [], error: new Error('entityType and entityId required') };
  return listAuditEvents({ entityType, entityId, limit });
}
